// Live counts for the navigation rail badges (Products / NFC tags), fetched
// once per session and shared across page mounts. Failures degrade to "no
// badge" — the rail never blocks on these.
import { api } from "./api";
import type { Product, NfcTag } from "./types";

export interface NavCounts {
  products: number;
  tags: number;
  // Capability signals — whether specialized surfaces are relevant to this org,
  // based on what the operator provisioned at onboarding (industry/product).
  giAuthorized: boolean;
  usesNfc: boolean;
}

let cached: Promise<NavCounts | null> | undefined;

export function getNavCounts(): Promise<NavCounts | null> {
  cached ??= Promise.all([
    api.get<Product[]>("/catalog/products").catch(() => [] as Product[]),
    api.get<{ tags: NfcTag[] | null }>("/nfc/tags").catch(() => ({ tags: null })),
    api.get<{ batches: unknown[] | null }>("/nfc/batches").catch(() => ({ batches: null })),
    api.get<{ producers: unknown[] | null }>("/gi/producers/my").catch(() => ({ producers: null })),
  ])
    .then(([products, tagResp, batchResp, giResp]) => {
      const tags = tagResp.tags?.length ?? 0;
      return {
        products: products?.length ?? 0,
        tags,
        usesNfc: tags > 0 || (batchResp.batches?.length ?? 0) > 0,
        giAuthorized: (giResp.producers?.length ?? 0) > 0,
      };
    })
    .catch(() => null);
  return cached;
}

/** Invalidate after mutations so the next shell mount refetches. */
export function invalidateNavCounts(): void {
  cached = undefined;
}

/** Compact count formatting for rail badges: 3412 -> "3.4k". */
export function formatCount(n: number): string {
  if (n >= 1000) {
    const k = n / 1000;
    return `${k >= 10 ? Math.round(k) : Math.round(k * 10) / 10}k`;
  }
  return String(n);
}
